import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { HeatmapView, type HeatmapData } from "./HeatmapView";
import { TrendChart } from "./TrendChart";
import { VisitorsView, type VisitorDetail, type VisitorRow } from "./VisitorsView";
import "./AnalyticsPage.css";

// アクセス解析（サーバー管理者のみ。IP アドレスを含むため）。データは app/analytics が自社サーバーに記録したもの

interface Summary {
  from: string;
  to: string;
  totals: { pageviews: number; visitors: number; tel: number; line: number; mail: number; map: number; form: number };
  daily: { date: string; pageviews: number; visitors: number }[];
  pages: { path: string; pageviews: number; visitors: number; avg_scroll: number }[] | null;
  sources: { source: string; kind: string; visits: number }[] | null;
  devices: { key: string; count: number }[] | null;
  conversions: { path: string; tel: number; line: number; mail: number; form: number }[] | null;
}

interface Visit {
  at: string;
  path: string;
  ip: string;
  device: string;
  referrer: string;
  visitor: string;
  user_agent: string;
  is_bot: boolean;
}

type Tab = "summary" | "heatmap" | "visitors" | "visits";

// 日本時間の日付（YYYY-MM-DD）
const jstDate = (d: Date) => new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Tokyo" }).format(d);

const PERIODS = [
  { days: 7, label: "直近 7 日" },
  { days: 30, label: "直近 30 日" },
  { days: 90, label: "直近 90 日" },
];

const DEVICE_LABEL: Record<string, string> = { pc: "パソコン", sp: "スマホ", tablet: "タブレット" };
const SOURCE_KIND: Record<string, string> = { direct: "直接", search: "検索", sns: "SNS", other: "ほかのサイト" };

const fmt = (n: number) => n.toLocaleString("ja-JP");

async function getJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "same-origin" });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error ?? `読み込みに失敗しました (${res.status})`);
  return body as T;
}

export const AnalyticsPage: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("summary");
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [visits, setVisits] = useState<Visit[] | null>(null);
  const [showBots, setShowBots] = useState(false);
  const [heatPath, setHeatPath] = useState("/");
  const [layout, setLayout] = useState<"pc" | "sp">("pc");
  const [heatmap, setHeatmap] = useState<HeatmapData | null>(null);
  const [visitorDate, setVisitorDate] = useState(() => jstDate(new Date()));
  const [visitorList, setVisitorList] = useState<VisitorRow[] | null>(null);
  const [selectedVisitor, setSelectedVisitor] = useState<string | null>(null);
  const [visitorDetail, setVisitorDetail] = useState<VisitorDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isServerAdmin = user?.role === "server_admin";

  const load = useCallback(async () => {
    if (!isServerAdmin) return;
    setLoading(true);
    setError(null);
    try {
      if (tab === "summary" || summary === null) {
        setSummary(await getJSON<Summary>(`/api/admin/analytics/summary?days=${days}`));
      }
      if (tab === "visits") {
        const v = await getJSON<{ visits: Visit[] }>(`/api/admin/analytics/visits?limit=300${showBots ? "&bots=1" : ""}`);
        setVisits(v.visits);
      }
      if (tab === "visitors") {
        const v = await getJSON<{ visitors: VisitorRow[] }>(`/api/admin/analytics/visitors?date=${visitorDate}`);
        setVisitorList(v.visitors);
        setVisitorDetail(
          selectedVisitor
            ? await getJSON<VisitorDetail>(
                `/api/admin/analytics/visitor?date=${visitorDate}&id=${encodeURIComponent(selectedVisitor)}`,
              )
            : null,
        );
      }
      if (tab === "heatmap") {
        setHeatmap(
          await getJSON<HeatmapData>(
            `/api/admin/analytics/heatmap?path=${encodeURIComponent(heatPath)}&layout=${layout}&days=${days}`,
          ),
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, days, showBots, heatPath, layout, isServerAdmin, visitorDate, selectedVisitor]);

  useEffect(() => {
    void load();
  }, [load]);

  const pageOptions = useMemo(() => {
    const paths = (summary?.pages ?? []).map((p) => p.path);
    return paths.includes(heatPath) ? paths : [heatPath, ...paths];
  }, [summary, heatPath]);

  if (!isServerAdmin) {
    return (
      <div className="an-page">
        <h1>アクセス解析</h1>
        <div className="an-error">この画面はサーバー管理者だけが利用できます。</div>
      </div>
    );
  }

  // 「最近の訪問」から、その日のその人の行動を開く
  const openVisitor = (v: Visit) => {
    setVisitorDate(jstDate(new Date(v.at)));
    setSelectedVisitor(v.visitor);
    setTab("visitors");
  };

  const openHeatmap = (path: string) => {
    setHeatPath(path);
    setTab("heatmap");
  };

  const maxPageviews = Math.max(1, ...(summary?.pages ?? []).map((p) => p.pageviews));
  const deviceTotal = (summary?.devices ?? []).reduce((a, d) => a + d.count, 0);

  return (
    <div className="an-page viz-root">
      <div className="an-header">
        <h1>アクセス解析</h1>
        <p className="an-muted">
          自社サーバーだけで記録しています（外部の解析サービスへの送信なし）。管理画面にログイン中の閲覧と、記録を拒否する設定のブラウザは含みません。
        </p>
      </div>

      <div className="an-tabs" role="tablist">
        {(
          [
            ["summary", "概要"],
            ["heatmap", "ヒートマップ"],
            ["visitors", "訪問者"],
            ["visits", "最近の訪問"],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key} className={tab === key ? "active" : ""} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* 絞り込みは 1 行にまとめ、下の表示すべてに効かせる */}
      <div className="an-filters">
        {tab !== "visits" && tab !== "visitors" && (
          <div className="an-segment" role="group" aria-label="期間">
            {PERIODS.map((p) => (
              <button key={p.days} aria-pressed={days === p.days} onClick={() => setDays(p.days)}>
                {p.label}
              </button>
            ))}
          </div>
        )}
        {tab === "heatmap" && (
          <>
            <label>
              ページ{" "}
              <select value={heatPath} onChange={(e) => setHeatPath(e.target.value)}>
                {pageOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <div className="an-segment" role="group" aria-label="表示">
              <button aria-pressed={layout === "pc"} onClick={() => setLayout("pc")}>
                パソコン表示
              </button>
              <button aria-pressed={layout === "sp"} onClick={() => setLayout("sp")}>
                スマホ表示
              </button>
            </div>
          </>
        )}
        {tab === "visitors" && (
          <label>
            日付{" "}
            <input
              type="date"
              value={visitorDate}
              max={jstDate(new Date())}
              onChange={(e) => {
                if (!e.target.value) return;
                setVisitorDate(e.target.value);
                setSelectedVisitor(null);
              }}
            />
          </label>
        )}
        {tab === "visits" && (
          <label>
            <input type="checkbox" checked={showBots} onChange={(e) => setShowBots(e.target.checked)} /> ボット（検索エンジンなど）も表示
          </label>
        )}
        {summary && tab !== "visits" && tab !== "visitors" && (
          <span className="an-muted">
            {summary.from} 〜 {summary.to}
          </span>
        )}
      </div>

      {error && <div className="an-error">{error}</div>}

      <div className={loading ? "an-body an-loading" : "an-body"}>
        {tab === "summary" && summary && (
          <>
            <div className="an-kpis">
              {[
                { label: "閲覧数", value: summary.totals.pageviews, note: "ページが表示された回数" },
                { label: "訪問者数", value: summary.totals.visitors, note: "日ごとの人数の合計（目安）" },
                { label: "電話のタップ", value: summary.totals.tel },
                { label: "LINE", value: summary.totals.line },
                { label: "フォーム送信", value: summary.totals.form },
                { label: "メール・地図", value: summary.totals.mail + summary.totals.map },
              ].map((k) => (
                <div key={k.label} className="an-kpi">
                  <div className="an-kpi-label">{k.label}</div>
                  <div className="an-kpi-value">{fmt(k.value)}</div>
                  {k.note && <div className="an-kpi-note">{k.note}</div>}
                </div>
              ))}
            </div>

            <section className="an-card">
              <h2>日別の推移</h2>
              <TrendChart
                dates={summary.daily.map((d) => d.date)}
                series={[
                  { key: "pv", label: "閲覧数", color: "var(--series-1)", values: summary.daily.map((d) => d.pageviews) },
                  { key: "uv", label: "訪問者数", color: "var(--series-2)", values: summary.daily.map((d) => d.visitors) },
                ]}
              />
            </section>

            <section className="an-card">
              <h2>ページ別</h2>
              {(summary.pages ?? []).length === 0 ? (
                <p className="an-muted">この期間の記録はまだありません。</p>
              ) : (
                <table className="an-table">
                  <thead>
                    <tr>
                      <th>ページ</th>
                      <th className="num">閲覧数</th>
                      <th className="num">訪問者数</th>
                      <th className="num">平均スクロール</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(summary.pages ?? []).map((p) => (
                      <tr key={p.path}>
                        <td>
                          <div className="an-ellipsis">{p.path}</div>
                          <div className="an-inline-bar" aria-hidden="true">
                            <span style={{ width: `${(p.pageviews / maxPageviews) * 100}%` }} />
                          </div>
                        </td>
                        <td className="num">{fmt(p.pageviews)}</td>
                        <td className="num">{fmt(p.visitors)}</td>
                        <td className="num">{p.avg_scroll >= 0 ? `${p.avg_scroll}%` : "-"}</td>
                        <td>
                          <button type="button" className="an-btn" onClick={() => openHeatmap(p.path)}>
                            ヒートマップ
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <div className="an-grid2">
              <section className="an-card">
                <h2>問い合わせにつながった行動（ページ別）</h2>
                {(summary.conversions ?? []).length === 0 ? (
                  <p className="an-muted">この期間はまだありません。</p>
                ) : (
                  <table className="an-table">
                    <thead>
                      <tr>
                        <th>押されたページ</th>
                        <th className="num">電話</th>
                        <th className="num">LINE</th>
                        <th className="num">メール</th>
                        <th className="num">フォーム</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary.conversions ?? []).map((c) => (
                        <tr key={c.path}>
                          <td className="an-ellipsis">{c.path}</td>
                          <td className="num">{fmt(c.tel)}</td>
                          <td className="num">{fmt(c.line)}</td>
                          <td className="num">{fmt(c.mail)}</td>
                          <td className="num">{fmt(c.form)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="an-card">
                <h2>どこから来たか</h2>
                {(summary.sources ?? []).length === 0 ? (
                  <p className="an-muted">この期間はまだありません。</p>
                ) : (
                  <table className="an-table">
                    <thead>
                      <tr>
                        <th>参照元</th>
                        <th>種類</th>
                        <th className="num">訪問</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary.sources ?? []).map((s) => (
                        <tr key={s.source}>
                          <td className="an-ellipsis">{s.source}</td>
                          <td>{SOURCE_KIND[s.kind] ?? s.kind}</td>
                          <td className="num">{fmt(s.visits)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <h2 style={{ marginTop: 24 }}>端末</h2>
                {(summary.devices ?? []).map((d) => (
                  <div key={d.key} className="an-device-row">
                    <span className="an-device-label">{DEVICE_LABEL[d.key] ?? d.key}</span>
                    <span className="an-inline-bar" aria-hidden="true">
                      <span style={{ width: `${deviceTotal ? (d.count / deviceTotal) * 100 : 0}%` }} />
                    </span>
                    <span className="num">
                      {fmt(d.count)}（{deviceTotal ? Math.round((d.count / deviceTotal) * 100) : 0}%）
                    </span>
                  </div>
                ))}
              </section>
            </div>
          </>
        )}

        {tab === "heatmap" && heatmap && <HeatmapView data={heatmap} />}

        {tab === "visitors" && visitorList && (
          <VisitorsView
            visitors={visitorList}
            selected={selectedVisitor}
            detail={visitorDetail}
            onSelect={(id) => setSelectedVisitor(id)}
          />
        )}

        {tab === "visits" && visits && (
          <section className="an-card">
            <p className="an-muted">
              新しい順に最大 300 件。IP アドレスとブラウザ情報は 90 日で自動的に消えます（それ以前の行は空欄）。
            </p>
            <table className="an-table">
              <thead>
                <tr>
                  <th>日時</th>
                  <th>ページ</th>
                  <th>IP アドレス</th>
                  <th>端末</th>
                  <th>参照元</th>
                  <th>訪問者 ID</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((v, i) => (
                  <tr key={i} title={v.user_agent}>
                    <td className="num">{new Date(v.at).toLocaleString("ja-JP")}</td>
                    <td className="an-ellipsis">{v.path}</td>
                    <td className="num">{v.ip || "-"}</td>
                    <td>
                      {DEVICE_LABEL[v.device] ?? v.device}
                      {v.is_bot && <span className="an-tag an-tag-other"> ボット</span>}
                    </td>
                    <td className="an-ellipsis">{v.referrer || "（直接）"}</td>
                    <td className="num">
                      {v.visitor && !v.is_bot ? (
                        <button type="button" className="an-link" onClick={() => openVisitor(v)} title="この日のこの人の行動を見る">
                          {v.visitor.slice(0, 8)}
                        </button>
                      ) : (
                        <span className="an-muted">{v.visitor.slice(0, 8)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
